import {
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import { driedTeaPurchaseKeys } from './use-dried-tea-purchase-query'
import type {
    CreateTeaPurchaseRequest,
    ListTeaPurchasesParams,
    PatchTeaPurchaseRequest,
} from '@/lib/api/tea-purchase-api'
import { teaPurchaseApi } from '@/lib/api/tea-purchase-api'
import { downloadBlob } from '@/lib/download-blob'
import { m } from '@/lib/paraglide/messages'

export const teaPurchaseKeys = {
    all: ['tea-purchases'] as const,
    list: (params: ListTeaPurchasesParams | undefined) =>
        [...teaPurchaseKeys.all, 'list', params ?? {}] as const,
    infinite: (params: Omit<ListTeaPurchasesParams, 'page'>) =>
        [...teaPurchaseKeys.all, 'infinite', params] as const,
    detail: (id: string) => [...teaPurchaseKeys.all, 'detail', id] as const,
}

export function useTeaPurchasesList(
    params?: ListTeaPurchasesParams,
    // opts.enabled: tắt fetch khi điều kiện tiên quyết chưa có (vd chưa chọn
    // hộ). Mặc định true — call-site cũ không đổi hành vi.
    opts?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: teaPurchaseKeys.list(params),
        enabled: opts?.enabled ?? true,
        queryFn: async () => {
            const res = await teaPurchaseApi.list(params)
            return res
        },
    })
}

const INFINITE_PAGE_SIZE = 50

/**
 * Phiếu cân tươi theo trang, tải thêm khi cuộn (picker + danh sách nạp sẵn
 * của modal tạo nhiều phiếu khô).
 */
export function useTeaPurchasesInfinite(
    params: Omit<ListTeaPurchasesParams, 'page' | 'per_page'>,
    opts?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        queryKey: teaPurchaseKeys.infinite(params),
        enabled: opts?.enabled ?? true,
        queryFn: async ({ pageParam }) => {
            const res = await teaPurchaseApi.list({
                ...params,
                page: pageParam,
                per_page: INFINITE_PAGE_SIZE,
            })
            return { items: res.data ?? [], meta: res.meta }
        },
        initialPageParam: 1,
        placeholderData: (prev) => prev,
        getNextPageParam: (lastPage) => {
            if (!lastPage.meta) return undefined
            return lastPage.meta.page < lastPage.meta.total_pages
                ? lastPage.meta.page + 1
                : undefined
        },
    })
}

export function useTeaPurchase(id: string | undefined) {
    return useQuery({
        queryKey: id
            ? teaPurchaseKeys.detail(id)
            : ['tea-purchases', 'detail', 'none'],
        enabled: !!id,
        queryFn: async () => {
            const res = await teaPurchaseApi.get(id ?? '')
            return res.data
        },
    })
}

/** Tải nhiều phiếu tươi theo id, dùng chung cache chi tiết với useTeaPurchase. */
export function useTeaPurchasesByIds(ids: readonly string[]) {
    return useQueries({
        queries: ids.map((id) => ({
            queryKey: teaPurchaseKeys.detail(id),
            queryFn: async () => {
                const res = await teaPurchaseApi.get(id)
                return res.data
            },
        })),
    })
}

export function useCreateTeaPurchase() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateTeaPurchaseRequest) =>
            teaPurchaseApi.create(data),
        onSuccess: () => {
            toast.success(m.tea_purchase_create_success())
            void qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
        },
        onError: () => {
            toast.error(m.tea_purchase_create_error())
        },
    })
}

export function usePatchTeaPurchase() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: PatchTeaPurchaseRequest
        }) => teaPurchaseApi.patch(id, data),
        onSuccess: () => {
            toast.success(m.tea_purchase_update_success())
            void qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_purchase_update_error())
        },
    })
}

export function useApproveTeaPurchase() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => teaPurchaseApi.approve(id),
        onSuccess: () => {
            toast.success(m.tea_purchase_approve_success())
            void qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_purchase_approve_error())
        },
    })
}

/**
 * Xoá phiếu cân tươi nhập sai. Lỗi chặn (409) mang message đầy đủ lý do từ BE
 * — luôn hiển thị nguyên văn, không nuốt thành message chung.
 */
export function useDeleteTeaPurchase() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => teaPurchaseApi.delete(id),
        onSuccess: () => {
            toast.success(m.tea_purchase_delete_success())
            void qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
            // Phiếu đã gộp: phiếu khô bị gỡ nguồn hoặc xoá theo (M-148).
            void qc.invalidateQueries({ queryKey: driedTeaPurchaseKeys.all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_purchase_delete_error())
        },
    })
}

export function useExportTeaPurchases() {
    return useMutation({
        mutationFn: async (params: ListTeaPurchasesParams) => {
            const { blob, filename } = await teaPurchaseApi.exportExcel(params)
            downloadBlob(blob, filename ?? 'PHIEU_CAN.xlsx')
        },
        onError: () => {
            toast.error(m.tea_purchase_export_failed())
        },
    })
}
